// src/pages/EdibleArtControl.tsx

import React, { useEffect, useState } from "react";
import { useNoIndex } from "@/hooks/useNoIndex";

type AuthState = "unknown" | "authed" | "denied";
const ADMIN_SESSION_KEY = "fca_admin_authed_v1";

export default function EdibleArtControl() {
  useNoIndex();

  const [pw, setPw] = useState("");
  const [authState, setAuthState] = useState<AuthState>("unknown");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "EdibleArtControl • Admin";
    const ok = localStorage.getItem(ADMIN_SESSION_KEY);
    if (ok === "1") setAuthState("authed");
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      // NOTE: update "/api/XXX" to your real single function path when you tell me which file it is
      const resp = await fetch("/api/seo?action=admin-auth", {
        method: "POST",
        credentials: "include", // <-- ensure Set-Cookie is stored & sent
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: pw }),
      });
      
      let data: any = null;
      const ctype = resp.headers.get("content-type") || "";
      if (ctype.includes("application/json")) {
        try { data = await resp.json(); } catch {}
      }
      
      if (resp.ok && data?.ok) {
        localStorage.setItem(ADMIN_SESSION_KEY, "1");
        setAuthState("authed");
      } else if (resp.status === 401) {
        setAuthState("denied");
        setErr("Incorrect Password");
      } else {
        const msg = (data && data.error) ? `Logging issue contact the Admin (${data.error})` : "Logging issue contact the Admin";
        setAuthState("denied");
        setErr(msg);
      }
    } catch (e: any) {
      setErr(e?.message || "Network error");
      setAuthState("denied");
    } finally {
      setBusy(false);
      setPw("");
    }
  }

  if (authState !== "authed") {
    return (
      <main className="min-h-screen flex items-center justify-center p-6 bg-gray-50">
        <div className="w-full max-w-sm rounded-2xl shadow-lg p-6 bg-white">
          <h1 className="text-xl font-semibold mb-4 text-center">Admin Access</h1>
          <form onSubmit={onSubmit} className="space-y-3">
            <label className="block">
              <span className="text-sm text-gray-700">Password</span>
              <input
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="mt-1 w-full rounded-xl border p-2 outline-none focus:ring"
                placeholder="Enter password"
                autoFocus
              />
            </label>
            {err && <p className="text-sm text-red-600">{err}</p>}
            <button
              disabled={busy || !pw}
              className="w-full rounded-2xl border px-4 py-2 font-medium hover:bg-gray-100 disabled:opacity-50"
            >
              {busy ? "Validating…" : "Enter"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen p-6 bg-gray-50">
      <div className="mx-auto max-w-2xl">
        <header className="mb-6">
          <h1 className="text-2xl font-semibold">EdibleArtControl</h1>
          <p className="text-sm text-gray-600">
            Internal admin panel. Not indexed and not linked.
          </p>
        </header>

        <section className="grid gap-4 sm:grid-cols-2">
          <a
            href="/image-prep-fca"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-2xl border bg-white p-4 shadow hover:shadow-md transition"
          >
            <div className="text-lg font-medium">ImagePrepFCA</div>
            <p className="text-sm text-gray-600">Open the image preparation workflow.</p>
          </a>
          
          <a
            href="/image-management"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-2xl border bg-white p-4 shadow hover:shadow-md transition"
          >
            <div className="text-lg font-medium">Image Management</div>
            <p className="text-sm text-gray-600">Upload & set the About section image.</p>
          </a>
          
          <a
            href="/theme-preview"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-2xl border bg-white p-4 shadow hover:shadow-md transition"
          >
            <div className="text-lg font-medium">Theme Preview</div>
            <p className="text-sm text-gray-600">Edit and preview themes safely.</p>
          </a>
          
          <a
            href="/recognitions-debug"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-2xl border bg-white p-4 shadow hover:shadow-md transition"
          >
            <div className="text-lg font-medium">Recognitions Debug</div>
            <p className="text-sm text-gray-600">Temporary debug view for Recognitions.</p>
          </a>
        
          <a
            href="/seo-maintenance"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-2xl border bg-white p-4 shadow hover:shadow-md transition"
          >
            <div className="text-lg font-medium">SEO Management</div>
            <p className="text-sm text-gray-600">Maintain titles, descriptions, and image metadata.</p>
          </a>
        </section>
      </div>
    </main>
  );
}